import React, { useEffect, useRef } from 'react';
import i18n from 'i18next';
import { RouterProvider } from 'react-router-dom';
import './App.css';
import AppMenu from './menu/AppMenu';
import { ThemeProvider } from '@mui/material/styles';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './components/ErrorFallback';
import * as authService from './auth/authService';
import { defaultDataDisplayPeriod as retrieveDefaultDataDisplayPeriod } from './subscription/subscriptionService';
import { useResourcesContext } from './resource/ResourceContext';
import '../node_modules/react-grid-layout/css/styles.css';
import '../node_modules/react-resizable/css/styles.css';
import router from './routes';
import theme from './theme';
import { initSmartDirection, refreshSmartDirection } from './helper/smartDirectionHelper';

const V2_LOGOUT_MESSAGE_TYPES = {
  COMPLETE: 'fieldpie:v2-logout-complete',
  FAILED: 'fieldpie:v2-logout-failed'
};

// TODO genel bir logging mekanizması, console.log, console.error yerine, hataları örneğin datadog'da görebileceğimiz bir yapı?

function App() {
  const { setLoggedInUserInfo, defaultDataDisplayPeriod, setDefaultDataDisplayPeriod } = useResourcesContext();
  const logoutHandshakeStartedRef = useRef(false);

  // Smart RTL/LTR input direction – initialize once at root level
  useEffect(() => {
    const cleanup = initSmartDirection();
    return cleanup;
  }, []);

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const shouldNotifyParentAfterLogout = currentUrl.pathname.endsWith('/logout') && currentUrl.searchParams.get('notifyParent') === 'true';

    async function logoutAndNotifyParent() {
      const v1Origin = new URL(process.env.REACT_APP_FIELDPIE_OLD_URL).origin;

      try {
        await authService.csrf();
        await authService.logout();

        window.parent.postMessage({ type: V2_LOGOUT_MESSAGE_TYPES.COMPLETE }, v1Origin);
      } catch (error) {
        console.log(error);

        window.parent.postMessage({ type: V2_LOGOUT_MESSAGE_TYPES.FAILED }, v1Origin);
      }
    }

    async function getInitialCsrfToken() {
      try {
        await authService.csrf();
      } catch (error) {
        console.log(error);
      }
    }

    async function getDefaultDataDisplayPeriod() {
      try {
        const period = await retrieveDefaultDataDisplayPeriod();
        setDefaultDataDisplayPeriod(period);
      } catch (error) {
        console.log(error);
      }
    }

    async function getLoggedInUserInfo() {
      try {
        const userInfo = await authService.getLoggedInUserInfo();
        setLoggedInUserInfo(userInfo.data);

        if (userInfo.data && userInfo.data.id && !defaultDataDisplayPeriod) {
          await getDefaultDataDisplayPeriod();
        }

        if (userInfo.data.language) {
          const language = userInfo.data.language.split('_')[0];
          i18n.changeLanguage(language);
          document.documentElement.lang = language;
          localStorage.setItem('language', language);

          if (language === 'ar') {
            refreshSmartDirection();
          }
        }

        if (userInfo.data.menuClosed) {
          localStorage.setItem('menuClosed', userInfo.data.menuClosed);
        }

        if (!userInfo.data.id && !window.location.href.endsWith('/login') && !window.location.href.includes('/v1bridge')) {
          window.location.href = `${process.env.REACT_APP_FIELDPIE_OLD_URL}/logout/`;
        }

        if (window.location.href.endsWith('/logout')) {
          await authService.logout();
        }
      } catch (error) {
        console.log(error);
      }
    }

    if (shouldNotifyParentAfterLogout) {
      if (!logoutHandshakeStartedRef.current) {
        logoutHandshakeStartedRef.current = true;
        logoutAndNotifyParent();
      }

      return;
    }

    getInitialCsrfToken();
    const isTrialOnboardingVideoPlayerBridge =
      currentUrl.pathname.endsWith('/v1bridge') && currentUrl.searchParams.get('component') === 'trialonboardingvideoplayer';
    if (!isTrialOnboardingVideoPlayerBridge) {
      getLoggedInUserInfo();
    }
  }, []);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={(details) => {
        // TODO Reset the state of your app so the error doesn't happen again
      }}
    >
      <iframe id="v1KeepAliveIFrame" src={`${process.env.REACT_APP_FIELDPIE_OLD_URL}/keepalive/`} width="0px" height="0px" style={{ display: 'none' }} />
      <ThemeProvider theme={theme}>
        <AppMenu>
          <RouterProvider router={router} />
        </AppMenu>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
